import { zodResolver } from '@hookform/resolvers/zod';
import type { UseFormRegister } from 'react-hook-form';
import { useSession } from '@hooks/security/use-session';
import { useAssessLiverHealth } from '@hooks/register/screening/use-assess-liver-health';
import { useLocalize } from '@hooks/use-localize';
import type { TLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { assessLiverHealthSchema } from '@views/register/screening/assess-liver-health.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import { asString } from '@/utils/parse';
import type {
    IAssessLiverHealthFormValues,
    IAssessLiverHealthModel,
    IAssessLiverHealthSubmitResult,
    TMedicalHistoryStatus,
} from '@/types/screening/assess-liver-health';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

const AssessLiverHealth = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessLiverHealth(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessLiverHealthContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAssessLiverHealthContentProps {
    model: IAssessLiverHealthModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessLiverHealthFormValues) => Promise<IAssessLiverHealthSubmitResult>;
}

const AssessLiverHealthContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessLiverHealthContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAssessLiverHealthFormValues, IAssessLiverHealthSubmitResult>({
        resolver: zodResolver(assessLiverHealthSchema),
        defaultValues: {
            historyLiverDisease: asString(model.historyLiverDisease) as TMedicalHistoryStatus,
            hasHepB: asString(model.hasHepB) as TMedicalHistoryStatus,
            hasHepC: asString(model.hasHepC) as TMedicalHistoryStatus,
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        formState: { errors },
    } = form;

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.AssessLiverHealth}
            title={t('DashLabel_ProgressLiverDisease', 'Liver Disease')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <HistoryStatusQuestion
                name="historyLiverDisease"
                label={t('DashLabel_DoYouHaveAHistoryOfLiverDisease', 'Do you have a history of Liver Disease')}
                error={errors.historyLiverDisease?.message}
                register={register}
                t={t}
            />

            <HistoryStatusQuestion
                name="hasHepB"
                label={t('DashLabel_DoYouHaveAHistoryOfHepatitisB', 'Do you have a history of Hepatitis B')}
                error={errors.hasHepB?.message}
                register={register}
                t={t}
            />

            <HistoryStatusQuestion
                name="hasHepC"
                label={t('DashLabel_DoYouHaveAHistoryOfHepatitisC', 'Do you have a history of Hepatitis C')}
                error={errors.hasHepC?.message}
                register={register}
                t={t}
            />
        </StepFormLayout>
    );
};

interface IHistoryStatusQuestionProps {
    name: keyof IAssessLiverHealthFormValues;
    label: string;
    error: string | undefined;
    register: UseFormRegister<IAssessLiverHealthFormValues>;
    t: TLocalize;
}

const HistoryStatusQuestion = ({ name, label, error, register, t }: IHistoryStatusQuestionProps) => (
    <div className="form-group">
        <label className="control-label col-xs-12">
            {label}?<FieldError message={error} />
        </label>
        <div className="col-xs-12" style={{ marginLeft: 10 }}>
            <label className="radio-inline">
                <input type="radio" value="0" {...register(name)} /> Never
            </label>
            <label className="radio-inline">
                <input type="radio" value="2" {...register(name)} />{' '}
                {t('DashLabel_TreatedInThePast', 'Treated in the past')}
            </label>
            <label className="radio-inline">
                <input type="radio" value="3" {...register(name)} />{' '}
                {t('DashLabel_StillBeingTreated', 'Still being treated')}
            </label>
            <label className="radio-inline">
                <input type="radio" value="-1" {...register(name)} />{' '}
                {t('DashLabel_IDonTKnow', "I don't know")}
            </label>
        </div>
    </div>
);

export default AssessLiverHealth;
